import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AudienceService } from '../services/audience.service';

/**
 * AudienceGuard - Route guard for audience-based access control
 * 
 * Validates that the audience in the URL path matches the domain's audience.
 * If mismatched, redirects to the domain's homepage.
 * 
 * Usage in routes:
 * { path: ':audience/ref', canActivate: [audienceGuard], ... }
 */
export const audienceGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const audienceService = inject(AudienceService);
  const router = inject(Router);

  // Get audience from URL parameter
  const urlAudience = route.paramMap.get('audience');

  // If no audience in URL, allow (will be handled by component)
  if (!urlAudience) {
    return true;
  }

  // Validate audience matches domain
  if (audienceService.isValidAudience(urlAudience)) {
    return true;
  }

  // Audience mismatch - redirect to domain's homepage
  console.warn(
    `🚫 Access denied: URL audience "${urlAudience}" does not match domain audience "${audienceService.audience}". ` +
    `Redirecting to homepage.`
  );

  return router.createUrlTree([audienceService.homepagePath]);
};

/**
 * HomepageRedirectGuard - Redirects root URL to audience-specific homepage
 * 
 * When user visits "/" it redirects to "/highschool" or "/highered"
 * based on the detected domain.
 */
export const homepageRedirectGuard: CanActivateFn = () => {
  const audienceService = inject(AudienceService);
  const router = inject(Router);

  // Redirect to audience-specific homepage
  return router.createUrlTree([audienceService.homepagePath]);
};

